import { Checkbox, DatePicker, Divider, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { DATE_FORMATS, GENDERS } from '../../../../../../common/constants';
import { formValidatorRules } from '../../../../../../common/utils';
import ModalComponent from '../../../../../../components/ModalComponent';

const { Item } = Form;
const { required } = formValidatorRules;

export const toDateOfBirthPayload = (value) =>
  value ? `${value?.format('YYYY-MM-DD')}T00:00:00.000Z` : undefined;

const ChildProfileModal = ({
  isModalOpen,
  childProfile,
  hasDefaultChild,
  loading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);
  const isEdit = !!childProfile?.id;
  const canMakeDefault = isEdit
    ? !childProfile?.isDefault && !!childProfile?.isActive
    : !hasDefaultChild;
  const isDobLocked =
    isEdit && !!childProfile?.dateOfBirth && !childProfile?.isDobUpdatable;

  useEffect(() => {
    if (!isModalOpen) return;
    setIsValidateTrigger(false);
    if (isEdit) {
      form?.setFieldsValue({
        firstName: childProfile?.firstName,
        lastName: childProfile?.lastName,
        gender: childProfile?.gender,
        dateOfBirth: childProfile?.dateOfBirth
          ? dayjs(childProfile?.dateOfBirth)
          : undefined,
        isDefault: false,
      });
    } else {
      form?.resetFields();
      form?.setFieldsValue({ isDefault: !hasDefaultChild });
    }
  }, [isModalOpen, childProfile]);

  const handleFinish = (values) => {
    onSubmit({
      firstName: values?.firstName?.trim(),
      lastName: values?.lastName?.trim() || undefined,
      gender: values?.gender,
      ...(!isDobLocked && {
        dateOfBirth: toDateOfBirthPayload(values?.dateOfBirth),
      }),
      ...(!isEdit && { isDefault: !!values?.isDefault }),
      ...(isEdit && values?.isDefault && { isDefault: true }),
    });
  };

  return (
    <ModalComponent
      open={isModalOpen}
      title={isEdit ? 'Edit Child Profile' : 'Add Child Profile'}
      okText={isEdit ? 'Save' : 'Add'}
      onOk={form?.submit}
      onCancel={onCancel}
      confirmLoading={loading}
      cancelButtonProps={{ disabled: loading }}
      destroyOnClose
      forceRender
    >
      <Divider />
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleFinish}
        validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
        onFinishFailed={() => setIsValidateTrigger(true)}
      >
        <Item
          label="First Name"
          name="firstName"
          rules={[{ ...required, message: 'Please enter first name!' }]}
        >
          <Input allowClear placeholder="Enter first name" maxLength={100} />
        </Item>
        <Item label="Last Name" name="lastName">
          <Input allowClear placeholder="Enter last name" maxLength={100} />
        </Item>
        <Item
          label="Gender"
          name="gender"
          rules={[{ required: true, message: 'Please select gender!' }]}
        >
          <Select placeholder="Select gender" options={GENDERS} />
        </Item>
        <Item
          label="Date of Birth"
          name="dateOfBirth"
          rules={[{ required: true, message: 'Please select date of birth!' }]}
          tooltip={
            isDobLocked
              ? 'Date of birth can no longer be changed for this child.'
              : undefined
          }
        >
          <DatePicker
            className="full-width"
            format={DATE_FORMATS?.REGULAR}
            disabled={isDobLocked}
            disabledDate={(current) => current && current > dayjs()}
          />
        </Item>
        {(!isEdit || canMakeDefault) && (
          <Item
            name="isDefault"
            valuePropName="checked"
            extra={
              canMakeDefault
                ? 'Roadmap toys are picked for the default child.'
                : 'This subscriber already has a default child. Use "Set as Default" on a profile to switch.'
            }
          >
            <Checkbox disabled={!canMakeDefault}>Make default child</Checkbox>
          </Item>
        )}
      </Form>
    </ModalComponent>
  );
};

export default ChildProfileModal;
